import React, { useState, useEffect, forwardRef, useRef } from "react";
import useDebounce from "../../Hooks/useDebounce";
import { Button } from "react-bootstrap";
import styled from "styled-components";
import classNames from "classnames";
import IBuildrProps from "../../../utilities/IBuildrProps";
import searchIcon from "./../../../icons/icon-search-struck-white-sm.svg";
import searchIconBlack from "../../../icons/icon-search-black.svg";
import { createDataTestId, AutomationPlacement, AutomationTestIdFeature, AutomationTestIdElementType } from '../../../utilities/automation';
import { useSearchContext } from "../SearchProvider";
import { ISearchResult } from "../models/ISearchResult";
import { ISearchProps } from "../models/ISearchProps";
import { solrSearch } from "../services/solrSearch";

const FormWrapper = styled.div<{ lineColor: string }>`
    .search-heading {
        color: #000;
        font-family: 'National2 Narrow';
        font-size: 0.88889rem;
        font-weight: 700;
        line-height: 1.55556rem;
        letter-spacing: 0.02667rem;
        text-transform: uppercase;
    }

    .form-control {
        min-height: 48px;
        overflow: hidden;
        padding-bottom: 11px;
        font-size: 0.77778rem;
        font-weight: 400;
        line-height: 1.11111rem;
    }

    .search-button {
        background: transparent url(${searchIcon}) center center no-repeat;
        background-size: 24px 24px;
        box-shadow: none;
        margin-right: 5px;
        padding-left: 1.167rem;
        padding-right: 1.167rem;
        &.black {
            background: transparent url(${searchIconBlack}) center center no-repeat;
        }
    }

    .dropdown {
        position: relative;

        .dropdown-toggle:focus {
            box-shadow: 0 0 0 0.2rem rgba(184, 18, 55, 0) !important;
            outline-offset: -2px;
        }

        .dropdown-toggle::after, &.show button::after  {
            text-align: right;
            transition: transform 0.3s cubic-bezier(0.52, -0.27, 0.39, 1.32);
            transform: rotate(0deg);
            vertical-align: middle;
            font-size: 24px;
        }
        &.show button::after {
            transform: rotate(180deg);
        }

        .dropdown-menu {
            .dropdown-item {
                white-space: normal;
                word-break: break-word;
            }
        }
    }

    .search-input-container::before {
        content: "";
        position: absolute;
        display: block;
        height: 2px;
        background-color: ${props => props.lineColor || `rgb(255, 255, 255)`};
        bottom: 13px;
        left: 19px;
        right: 48px;
        z-index: 1;
    }

    .input-group {
        margin-bottom: 0;
        height: 100%;
        color: #fff;

        .form-control:focus {
            box-shadow: 0 0 0 0.2rem rgba(184, 18, 55, 0) !important;
        }

        input, input:focus {
            padding-left: 20px;
            padding-right: 5px;
            font-size: 0.889rem;
            height: 100%;
        }

        input::placeholder {
            color: inherit;
        }
    }
`;

export interface ISearchFormProps extends IBuildrProps {
    containerClass?: string;
    buttonClass?: string;
    lineColor?: string;
    inputPlaceholder?: string;
    inputClass?: string;
    pageType?: "blog" | "help" | "newsroom" | null;
    focus?: boolean;
    enableRedirects?: boolean;
    placement?: AutomationPlacement;
    limit?: number;
    preventSubmit?: boolean;
    executeSearch?: (searchProps: ISearchProps) => Promise<ISearchResult[]>;
}

const SearchForm = forwardRef((props: ISearchFormProps, ref: any) => {
    const { inputPlaceholder = "", cmsId, className, editorClassObj, focus, preventSubmit = false, inputClass = "text-white bg-transparent", containerClass = "rounded-pill bg-redRock", placement, limit = 0, enableRedirects = true, executeSearch = solrSearch } = props;
    const { results, setResults, showResults, setShowResults, setStatus, selected, setDebouncedSearchValue = () => {} } = useSearchContext();
    const [displayValue, setDisplayValue] = useState("");
    const debouncedSearchTerm = useDebounce(displayValue, 500);
    const inputRef = useRef<HTMLInputElement>();

    // If focus prop is true, focus the search input
    useEffect(() => {
        if (focus) {
            inputRef.current.focus();
        }
    }, [focus]);

    useEffect(() => {
        async function getResults() {
            try {
                setStatus({hasError: false, selected: null, isLoading: true});
                const results = await executeSearch({ query: debouncedSearchTerm, pageType: props.pageType, limit });
                populateResults(results);
                setStatus({hasError: false, selected: null, isLoading: false});
            } catch (error) {
                console.error(`Failed to populate search results`, error);
                populateResults([]);
                setStatus({hasError: true, selected: null, isLoading: false});
            }
        }
        if (debouncedSearchTerm) {
            if (selected !== debouncedSearchTerm) {
                getResults();
            }
        } else {
            clearResults();
        }
        setDebouncedSearchValue(debouncedSearchTerm);

    }, [debouncedSearchTerm]);

    useEffect(() => {
        if (selected) {
            setDisplayValue(selected);
            setShowResults(false);
            inputRef.current.blur();
        }
    }, [selected]);

    function populateResults(results) {
        setResults(results || []);
        setShowResults(true);
    }

    function clearResults() {
        setResults(null);
        setShowResults(false);
    }

    function doSearch(pageType = null) {
        if (!enableRedirects || preventSubmit) return;
        const pageTypeParam = pageType ? `&pageType=${pageType}` : '';
        if (displayValue) {
            window.location.href = `/${pageType === "blog" ? "must-reads" : "search"}?q=${displayValue}${pageTypeParam}`;
        }
    }

    function toggleResults(show: boolean) {
        if (!show && showResults) {
            setShowResults(false);
        }
        if (show && !showResults && results) {
            setShowResults(true);
        }
    }

    function handleInput(e: any) {
        setDisplayValue(e.target.value);
    }

    function handleEnter(event) {
        if (event.keyCode === 13 && !preventSubmit) {
            doSearch(props.pageType);
        }
    }

    function handleBlur(e) {
        const currentTarget = e.currentTarget;

        // Check the newly focused element in the next tick of the event loop
        setTimeout(() => {
            // Check if the new activeElement is a child of the original container
            if (!currentTarget.contains(document.activeElement)) {
                toggleResults(false);
            }
        }, 350);
    }

    return (
        <FormWrapper ref={ref} data-id={cmsId} className={classNames(className, editorClassObj, "w-100")} lineColor={props.lineColor}>
            <div onBlur={handleBlur} onFocus={() => toggleResults(true)}>
            <div className={classNames("search-input-container input-group", containerClass)}>
                    <input
                        data-test-id={createDataTestId({placement, feature: AutomationTestIdFeature.SEARCH, element: AutomationTestIdElementType.INPUT, specifier: 'searchText'})}
                        ref={inputRef}
                        onKeyUp={handleEnter}
                        value={displayValue}
                        onChange={handleInput}
                        type="text"
                        className={classNames("search-input form-control border-0", inputClass)}
                        placeholder={inputPlaceholder}
                        aria-label="Search Field"
                        />
                    <div className="input-group-append">
                        <Button
                            data-test-id={createDataTestId({placement, feature: AutomationTestIdFeature.SEARCH, element: AutomationTestIdElementType.BUTTON, specifier: 'search'})}
                            variant="link"
                            className={classNames("search-button", props.buttonClass)}
                            onClick={(() => doSearch(props.pageType))}
                            aria-label="Search Button"
                        />
                    </div>
                </div>
            </div>
        </FormWrapper>
    );
});

export default SearchForm;